import { TopalTransaction, TopalPosting } from './topal-xml-builder';
import { InvoiceGroup } from './topal-invoice-mapper';
import { TopalValidationError } from './topal-validation-error';

export interface PostingGroup {
  transaction: TopalTransaction;
  postings: TopalPosting[];
}

function getGrossForTaxLine(
  transactionAmount: number,
  tax: { totalAmount?: number | null },
  taxCount: number,
): number {
  if (taxCount > 1 && tax.totalAmount != null) {
    return Math.round(tax.totalAmount * 100) / 100;
  }
  return Math.round(transactionAmount * 100) / 100;
}

function computeVatAmount(
  gross: number,
  tax: { amount?: number | null; taxRate: { rate: number } },
  taxCount: number,
): number {
  if (taxCount > 1 && tax.amount != null) {
    return Math.round(tax.amount * 100) / 100;
  }
  return Math.round((gross * tax.taxRate.rate) / (100 + tax.taxRate.rate) * 100) / 100;
}

function buildPostingText(
  title: string,
  currencyCode: string,
  amount: number,
  baseCurrency: string,
): string {
  if (!title) return '';
  const currencyUpper = currencyCode.toUpperCase();
  const alreadyHasCurrency = title.includes(`(${currencyUpper} `);
  const text =
    currencyUpper !== baseCurrency.toUpperCase() && !alreadyHasCurrency
      ? `${title} (${currencyUpper} ${amount.toFixed(2)})`
      : title;
  return text.substring(0, 64);
}

function resolveExchangeRate(
  exchangeRate: number | null | undefined,
  baseCurrencyAmount: number,
  amount: number,
): number {
  if (exchangeRate != null) return exchangeRate;
  if (amount === 0) {
    throw new TopalValidationError(
      'Wechselkurs kann nicht ermittelt werden: Originalbetrag ist 0',
    );
  }
  return baseCurrencyAmount / amount;
}

function validateForeignCurrency(
  currencyCode: string,
  baseCurrency: string,
  baseCurrencyAmount: number | null | undefined,
  docNum: number,
): void {
  if (
    currencyCode.toUpperCase() !== baseCurrency.toUpperCase() &&
    baseCurrencyAmount == null
  ) {
    throw new TopalValidationError(
      `Fremdwährungstransaktion (${currencyCode}) ohne Basiswährungsbetrag für Beleg Nr. ${docNum}`,
    );
  }
}

export function mapInvoiceGroupToPostings(
  invoiceGroup: InvoiceGroup,
  sammelkontoCode: string,
  baseCurrency: string,
): PostingGroup {
  const transaction: TopalTransaction = {
    docNum: invoiceGroup.docNum,
    docDate: invoiceGroup.invoice.invoiceDate,
    transactionTypeFID: 3,
  };

  // Determine if invoice group has foreign currency transactions
  const firstTransaction = invoiceGroup.transactions[0];
  const isForeignGroup =
    firstTransaction &&
    firstTransaction.currencyCode.toUpperCase() !== baseCurrency.toUpperCase();
  const groupExchangeRate = isForeignGroup
    ? resolveExchangeRate(
        firstTransaction.exchangeRate,
        firstTransaction.baseCurrencyAmount ?? firstTransaction.amount,
        firstTransaction.amount,
      )
    : undefined;

  const creditPosting: TopalPosting = {
    docNum: invoiceGroup.docNum,
    freeInvoiceNum: invoiceGroup.invoice.freeInvoiceNum,
    accountCode: sammelkontoCode,
    postingTypeFID: 2,
    amount: invoiceGroup.invoice.totalAmount,
    isDebit: false,
    text: invoiceGroup.invoice.text,
    ...(isForeignGroup && groupExchangeRate !== undefined && {
      exchangeRate: groupExchangeRate,
      fcAmount: Math.round(
        invoiceGroup.transactions.reduce(
          (sum, transaction) => sum + transaction.amount,
          0,
        ) * 100,
      ) / 100,
    }),
  };

  const debitPostings: TopalPosting[] = [];

  for (const transactionItem of invoiceGroup.transactions) {
    validateForeignCurrency(
      transactionItem.currencyCode,
      baseCurrency,
      transactionItem.baseCurrencyAmount,
      invoiceGroup.docNum,
    );

    const isForeign =
      transactionItem.currencyCode.toUpperCase() !== baseCurrency.toUpperCase();
    const baseCurrencyGross =
      transactionItem.baseCurrencyAmount ?? transactionItem.amount;
    const exchangeRateResolved = isForeign
      ? resolveExchangeRate(
          transactionItem.exchangeRate,
          baseCurrencyGross,
          transactionItem.amount,
        )
      : undefined;

    if (transactionItem.taxes.length === 0) {
      debitPostings.push({
        docNum: invoiceGroup.docNum,
        freeInvoiceNum: invoiceGroup.invoice.freeInvoiceNum,
        accountCode: transactionItem.bookingAccount.accountNumber,
        postingTypeFID: 2,
        amount: baseCurrencyGross,
        isDebit: true,
        text: buildPostingText(
          transactionItem.title,
          transactionItem.currencyCode,
          transactionItem.amount,
          baseCurrency,
        ),
        ...(isForeign && exchangeRateResolved !== undefined && {
          exchangeRate: exchangeRateResolved,
          fcAmount: transactionItem.amount,
        }),
      });
    } else {
      const taxCount = transactionItem.taxes.length;

      for (const tax of transactionItem.taxes) {
        const isNoExport = tax.taxRate.code === 'NO_EXPORT';

        // For foreign multi-tax, convert tax.totalAmount to base currency
        const originalGross = getGrossForTaxLine(
          transactionItem.amount,
          tax,
          taxCount,
        );

        let gross: number;
        if (isForeign && taxCount > 1 && tax.totalAmount != null) {
          gross = Math.round(tax.totalAmount * exchangeRateResolved! * 100) / 100;
        } else {
          gross = getGrossForTaxLine(baseCurrencyGross, tax, taxCount);
        }

        if (isNoExport) {
          debitPostings.push({
            docNum: invoiceGroup.docNum,
            freeInvoiceNum: invoiceGroup.invoice.freeInvoiceNum,
            accountCode: transactionItem.bookingAccount.accountNumber,
            postingTypeFID: 2,
            amount: gross,
            isDebit: true,
            text: buildPostingText(
              transactionItem.title,
              transactionItem.currencyCode,
              transactionItem.amount,
              baseCurrency,
            ),
            ...(isForeign && exchangeRateResolved !== undefined && {
              exchangeRate: exchangeRateResolved,
              fcAmount: originalGross,
            }),
          });
        } else {
          // Single-tax: drop stale tax.amount, recompute from rate (mirrors
          // taxCount === 1 branch of computeVatAmount). Multi-tax: trust
          // OCR-extracted per-line tax.amount.
          const originalVatAmount =
            taxCount === 1
              ? Math.round(
                  ((originalGross * tax.taxRate.rate) /
                    (100 + tax.taxRate.rate)) *
                    100,
                ) / 100
              : tax.amount;

          let vatAmount: number;
          if (isForeign && originalVatAmount != null) {
            const convertedTaxAmount =
              Math.round(originalVatAmount * exchangeRateResolved! * 100) /
              100;
            vatAmount = computeVatAmount(
              gross,
              {
                amount: convertedTaxAmount,
                taxRate: tax.taxRate,
              },
              taxCount,
            );
          } else {
            vatAmount = computeVatAmount(gross, tax, taxCount);
          }

          const net = Math.round((gross - vatAmount) * 100) / 100;

          // Compute FC amounts for foreign currency
          const originalNet = Math.round((originalGross - (originalVatAmount ?? 0)) * 100) / 100;

          debitPostings.push({
            docNum: invoiceGroup.docNum,
            freeInvoiceNum: invoiceGroup.invoice.freeInvoiceNum,
            accountCode: transactionItem.bookingAccount.accountNumber,
            postingTypeFID: 2,
            amount: net,
            isDebit: true,
            text: buildPostingText(
              transactionItem.title,
              transactionItem.currencyCode,
              transactionItem.amount,
              baseCurrency,
            ),
            vatCode: tax.taxRate.code,
            vatAmount,
            vatPercent: tax.taxRate.rate,
            ...(isForeign && exchangeRateResolved !== undefined && {
              exchangeRate: exchangeRateResolved,
              fcAmount: originalNet,
              ...(originalVatAmount != null && {
                vatFcAmount: originalVatAmount,
              }),
            }),
          });
        }
      }

      // Remainder method for multi-tax foreign currency
      if (isForeign && taxCount > 1) {
        const transactionDebits = debitPostings.slice(-taxCount);
        const debitSum = transactionDebits.reduce(
          (sum, posting) => sum + posting.amount + (posting.vatAmount ?? 0),
          0,
        );
        const diff = Math.round((baseCurrencyGross - debitSum) * 100) / 100;
        if (Math.abs(diff) > 0 && transactionDebits.length > 0) {
          const lastDebit = transactionDebits[transactionDebits.length - 1];
          lastDebit.amount = Math.round((lastDebit.amount + diff) * 100) / 100;
        }
      }
    }
  }

  // D-15: Balance validation
  const creditAmount = creditPosting.amount;
  const debitTotal = debitPostings.reduce(
    (sum, posting) => sum + posting.amount + (posting.vatAmount ?? 0),
    0,
  );

  if (Math.abs(creditAmount - debitTotal) > 0.005) {
    throw new TopalValidationError(
      `Buchungen unausgeglichen für Beleg Nr. ${invoiceGroup.docNum}: Haben=${creditAmount.toFixed(2)}, Soll-Summe=${debitTotal.toFixed(2)}`,
    );
  }

  return { transaction, postings: [creditPosting, ...debitPostings] };
}
